import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Modal, ModalBody} from 'reactstrap';
import moment from 'moment';
import {InformesApi, listOf} from '../../../api/informesApi';
import {Spinner} from '../../Spinner';
import {anchorAttr} from '../../../guides/anchors';

export const SCOPE_CLIENT = 'CLIENT';
export const SCOPE_ALL = 'ALL';

/**
 * El selector de informes (mockup + la paleta Cmd+K de las vistas de hoy): «Informe nuevo»,
 * pestañas Este cliente / Todos los clientes, buscador, pill EN PORTAL inline. En «Todos»
 * abrir un informe cambia de cliente — lo decide quien recibe `onOpen`.
 */
export function InformePicker({isOpen, toggle, agencyClientId, agencyClients, currentInformeId, onNew, onOpen}) {
    const {t} = useTranslation();
    const [scope, setScope] = useState(SCOPE_CLIENT);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [items, setItems] = useState([]);
    const [highlight, setHighlight] = useState(0);

    const clientName = useMemo(() => {
        const byId = {};
        (agencyClients || []).forEach((c) => { byId[String(c.id)] = c.name; });
        return (id) => byId[String(id)] || '';
    }, [agencyClients]);

    useEffect(() => {
        if (!isOpen) return undefined;
        let alive = true;
        setLoading(true);
        setError(false);
        const request = scope === SCOPE_ALL ? InformesApi.listAll() : InformesApi.list(agencyClientId);
        request.then((r) => {
            if (!alive) return;
            setLoading(false);
            if (!r || r.status !== 'OK') { setError(true); setItems([]); return; }
            setItems(listOf(r, 'client_reports'));
            setHighlight(0);
        });
        return () => { alive = false; };
    }, [isOpen, scope, agencyClientId]);

    useEffect(() => { if (isOpen) { setSearch(''); setScope(SCOPE_CLIENT); } }, [isOpen]);

    const needle = search.trim().toLowerCase();
    const filtered = items.filter((i) => !needle || (i.name || '').toLowerCase().includes(needle)
        || clientName(i.agency_client_id).toLowerCase().includes(needle));

    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, filtered.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
        else if (e.key === 'Enter' && filtered[highlight]) { e.preventDefault(); onOpen(filtered[highlight]); }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} className="inf-picker-modal" data-testid="informe-picker">
            <ModalBody>
                <div className="inf-picker">
                    <button type="button" className="inf-picker-new" onClick={onNew} data-testid="picker-new">
                        <span aria-hidden="true">+</span> {t('informes.picker.new', 'Informe nuevo')}
                    </button>
                    <div className="inf-picker-scope" role="tablist">
                        <button type="button" role="tab" aria-selected={scope === SCOPE_CLIENT}
                                className={`inf-picker-tab ${scope === SCOPE_CLIENT ? '-active' : ''}`} onClick={() => setScope(SCOPE_CLIENT)}>
                            {t('informes.picker.this_client', 'Este cliente')}
                        </button>
                        <button type="button" role="tab" aria-selected={scope === SCOPE_ALL}
                                className={`inf-picker-tab ${scope === SCOPE_ALL ? '-active' : ''}`} onClick={() => setScope(SCOPE_ALL)}>
                            {t('informes.picker.all_clients', 'Todos los clientes')}
                        </button>
                    </div>
                    <input type="search" className="inf-picker-search" autoFocus value={search} onKeyDown={onKeyDown}
                           placeholder={t('informes.picker.search', 'Buscar un informe…')}
                           onChange={(e) => { setSearch(e.target.value); setHighlight(0); }}/>
                    {loading && <div className="box-loader"><Spinner color="positive small"/></div>}
                    {!loading && error && <div className="inf-panel-state -error">{t('informes.picker.error', 'No se pudieron cargar los informes.')}</div>}
                    {!loading && !error && filtered.length === 0 && (
                        <div className="inf-panel-state">
                            {needle ? t('informes.picker.no_match', 'Ningún informe coincide.') : t('informes.picker.empty', 'Todavía no hay informes guardados.')}
                        </div>
                    )}
                    {!loading && !error && filtered.length > 0 && (
                        <ul className="inf-picker-list" role="listbox" {...anchorAttr('informes_picker_list')}>
                            {filtered.map((informe, index) => (
                                <li key={informe.id} role="option" aria-selected={index === highlight}
                                    className={`inf-picker-row ${index === highlight ? '-highlight' : ''} ${informe.id === currentInformeId ? '-current' : ''}`}
                                    onMouseEnter={() => setHighlight(index)} onClick={() => onOpen(informe)}>
                                    <span className="inf-picker-name">{informe.name}</span>
                                    {scope === SCOPE_ALL && <span className="inf-picker-client">{clientName(informe.agency_client_id)}</span>}
                                    {informe.is_published && <span className="inf-pill -portal">{t('informes.pill.published', 'EN PORTAL')}</span>}
                                    {informe.mode === 'SNAPSHOT' && <span className="inf-pill -snapshot">{t('informes.pill.snapshot', 'FOTO')}</span>}
                                    {informe.updated_on && <span className="inf-picker-date">{moment(informe.updated_on).format('DD/MM/YYYY')}</span>}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </ModalBody>
        </Modal>
    );
}
